const parseJson = (text, fallback) => {
  try {
    return JSON.parse(text) ?? fallback;
  } catch {
    return fallback;
  }
};

const alignPivots = (codes, valueSets, values) =>
  Object.fromEntries(
    codes.map((code) => [code, values.find((handle) => valueSets.get(code)?.has(handle)) ?? ''])
  );

const narrow = (products, code, value) =>
  products.filter((p) => p.pivots[code] === value);

const reachable = ([code, ...rest], selections, products) =>
  code === undefined
    ? []
    : [
        { code, values: new Set(products.map((p) => p.pivots[code]).filter(Boolean)) },
        ...reachable(rest, selections, narrow(products, code, selections[code] ?? '')),
      ];

const resolveChangedFirst = (codes, selections, products, changed) => {
  const order = changed ? [changed, ...codes.filter((code) => code !== changed)] : codes;
  return order.reduce((survivors, code) => {
    const value = selections[code];
    if (!value) return survivors;
    const next = narrow(survivors, code, value);
    if (next.length > 0) return next;
    return code === changed ? next : survivors;
  }, products);
};

const best = (products, currentHandle) =>
  [...products].sort(
    (a, b) =>
      (a.handle === currentHandle ? -1 : b.handle === currentHandle ? 1 : 0) ||
      Number(Boolean(b.available)) - Number(Boolean(a.available)) ||
      String(a.title).localeCompare(String(b.title))
  )[0] ?? null;

const PIVOT_VALUE_FRAGMENT = `... on Metaobject {
  handle
  label: field(key: "label") { value }
  swatchcolor: field(key: "swatchcolor") { value }
  swatchimage: field(key: "swatchimage") { reference { ... on MediaImage { image { url(transform: { maxWidth: 128, maxHeight: 128 }) } } } }
}`;

const VALUE_METAOBJECT_TYPE = 'displaygrouppivotvalue';

const pivotValueCache = new Map();

const streamAllReferences = async (token, config) => {
  if (!token || !window.MetaobjectStream) return null;

  const iterator = window.MetaobjectStream.streamReferences({ token, ...config });
  const nodes = [];
  for (;;) {
    let step;
    try {
      step = await iterator.next();
    } catch {
      return null;
    }
    if (step.done) break;
    if (!step.value?.ok) return null;
    step.value.nodes.forEach((node) => {
      if (node?.handle) nodes.push(node);
    });
  }
  return nodes;
};

const fetchPivotValues = (token, type, handle) => {
  const key = `${type}/${handle}`;
  if (pivotValueCache.has(key)) return pivotValueCache.get(key);

  const request = streamAllReferences(token, {
    metaobject: { type, handle },
    fieldKey: 'pivotvalues',
    pageSize: 50,
    nodeFragment: PIVOT_VALUE_FRAGMENT,
  }).then((nodes) => {
    if (nodes === null) pivotValueCache.delete(key);
    return nodes;
  });
  pivotValueCache.set(key, request);
  return request;
};

const PRODUCT_GRAPH_FRAGMENT = '... on Product { handle onlineStoreUrl availableForSale title }';

const displayGroupGraphPromises = new Map();
const displayGroupGraphResolved = new Map();

const buildDisplayGroupGraph = async (token, pivots) => {
  const handles = pivots.flatMap((pivot) => pivot.valueHandles);
  if (!handles.length || !token || !window.MetaobjectStream) return null;

  const productsByValue = await window.MetaobjectStream.fetchReferencesBatch({
    token,
    type: VALUE_METAOBJECT_TYPE,
    fieldKey: 'productlist',
    handles,
    nodeFragment: PRODUCT_GRAPH_FRAGMENT,
  });

  const products = new Map();
  pivots.forEach((pivot) => {
    pivot.valueHandles.forEach((valueHandle) => {
      (productsByValue.get(valueHandle) ?? []).forEach((node) => {
        if (!node?.handle) return;
        const entry =
          products.get(node.handle) ??
          {
            handle: node.handle,
            url: node.onlineStoreUrl || `/products/${node.handle}`,
            available: node.availableForSale,
            title: node.title,
            pivots: {},
            values: [],
          };
        entry.pivots[pivot.code] = valueHandle;
        entry.values.push(valueHandle);
        products.set(node.handle, entry);
      });
    });
  });

  return [...products.values()];
};

const ensureDisplayGroupGraph = (token, id, pivots) => {
  if (!id) return Promise.resolve(null);
  if (displayGroupGraphPromises.has(id)) return displayGroupGraphPromises.get(id);

  const promise = buildDisplayGroupGraph(token, pivots).then((graph) => {
    if (graph?.length) displayGroupGraphResolved.set(id, graph);
    else displayGroupGraphPromises.delete(id);
    return graph;
  });
  displayGroupGraphPromises.set(id, promise);
  return promise;
};

class ProductConfigurableOptions extends HTMLElement {
  constructor() {
    super();
    this.handleSelectorChange = this.handleSelectorChange.bind(this);
    this.handlePrintingChange = this.handlePrintingChange.bind(this);
    this.handleMoreColors = this.handleMoreColors.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handlePageShow = this.handlePageShow.bind(this);
    this.handlePopState = this.handlePopState.bind(this);
    this.handleStickyQuantityClick = this.handleStickyQuantityClick.bind(this);
  }

  connectedCallback() {
    this.form = document.getElementById(this.dataset.formId);
    this.quantityWrapper = this.querySelector('[data-configurable-quantity-wrapper]');
    this.quantityInput = this.querySelector('[data-configurable-quantity-input]');
    this.quantityDropdown = this.querySelector('[data-configurable-quantity]');
    this.submitButtons = document.querySelectorAll(
      `[type="submit"][form="${this.dataset.formId}"]`
    );

    this.stickyForm = Array.from(document.querySelectorAll('[data-configurable-sticky]')).find(
      (element) => element.getAttribute('form') === this.dataset.formId
    );
    this.stickyQuantity = this.stickyForm?.querySelector('[data-configurable-sticky-quantity]');
    this.stickyQuantityValue = this.stickyForm?.querySelector('[data-configurable-sticky-quantity-value]');
    this.stickyTotal = this.stickyForm?.querySelector('[data-configurable-sticky-total]');
    this.stickyFromPrice = this.stickyForm?.querySelector('[data-configurable-sticky-from-price]');
    this.mainSubmitButton = Array.from(document.querySelectorAll('[data-configurable-main-submit]')).find(
      (button) => button.getAttribute('form') === this.dataset.formId
    );
    this.mainSubmitPrice = this.mainSubmitButton?.querySelector('[data-configurable-main-price]');

    this.querySelectorAll('[data-configurable-control]').forEach((control) =>
      control.addEventListener('change', this.handleSelectorChange)
    );
    this.querySelectorAll('[data-configurable-printing-option]').forEach((option) =>
      option.addEventListener('change', this.handlePrintingChange)
    );
    this.querySelectorAll('[data-configurable-more]').forEach((button) =>
      button.addEventListener('click', this.handleMoreColors)
    );
    this.form?.addEventListener('submit', this.handleSubmit, true);
    this.stickyQuantity?.addEventListener('click', this.handleStickyQuantityClick);
    window.addEventListener('pageshow', this.handlePageShow);
    window.addEventListener('popstate', this.handlePopState);

    const markUserInteracted = (event) => { if (event.isTrusted) this.userInteracted = true; };
    this.addEventListener('pointerdown', markUserInteracted, true);
    this.addEventListener('keydown', markUserInteracted, true);
    this.addEventListener('change', markUserInteracted, true);

    this.currentValueHandles = new Set(
      (this.dataset.currentValues ?? '').split(',').filter(Boolean)
    );
    this.currentHandles = this.computeCurrentHandles();

    const cachedGraph = displayGroupGraphResolved.get(this.dataset.displayGroupId);
    this.graph = cachedGraph ?? (this.streamableGroups.length ? null : this.serverGraph);
    this.toggleAttribute('data-hydrating', !this.graph);

    this.setupQuantityDropdown();
    this.updatePurchaseTotals();
    this.updateSelectedLabels();
    this.updatePrintingState();
    this.updateAvailability();

    // Trips the theme's swatch-reveal CSS gate, which hides swatch lists until this attribute is set.
    this.setAttribute('data-swatches-sorted', '');

    this.graphReady = this.hydrateGraph();
  }

  computeCurrentHandles() {
    return new Map(
      this.groups.map(({ group, code }) => {
        const control =
          group.querySelector('select[data-configurable-control]')?.selectedOptions[0] ||
          group.querySelector('input[data-configurable-control]:checked');
        return [code, control?.dataset.pivotValueHandle];
      })
    );
  }

  get streamableGroups() {
    return this.groups.filter(({ group, code }) => {
      const total = Number.parseInt(group.dataset.valueTotal ?? '', 10);
      const rendered = this.valueHandlesByCode.get(code)?.size ?? 0;
      return Boolean(group.dataset.pivotHandle) && Number.isFinite(total) && total > rendered;
    });
  }

  async hydrateGraph() {
    const targets = this.streamableGroups;
    const token = this.dataset.storefrontToken;

    if (targets.length && token && window.MetaobjectStream) {
      await Promise.all(targets.map(({ group }) => this.streamPivotValues(group, token)));
      targets.forEach(({ group }) => this.syncCurrentSelection(group));
      this.currentHandles = this.computeCurrentHandles();
      this._serverGraph = undefined;
    }

    const hydrated = targets.length
      ? await ensureDisplayGroupGraph(token, this.dataset.displayGroupId, this.graphPivots)
      : this.serverGraph;

    this.graph = hydrated?.length ? hydrated : this.serverGraph;
    this.removeAttribute('data-hydrating');
    this.updateSelectedLabels();
    this.updateAvailability();
    return this.graph;
  }

  get graphPivots() {
    return this.groups.map(({ code }) => ({
      code,
      valueHandles: [...(this.valueHandlesByCode.get(code) ?? [])],
    }));
  }

  async streamPivotValues(group, token) {
    const handle = group.dataset.pivotHandle;
    const type = group.dataset.pivotType;
    const code = group.dataset.pivotCode;
    if (!handle || !type) return;

    const nodes = await fetchPivotValues(token, type, handle);
    if (!nodes) return;

    const isSelect = Boolean(group.querySelector('select[data-configurable-control]'));
    const moreButton = group.querySelector('[data-configurable-more]');
    const collapsed = Boolean(moreButton) && !moreButton.hidden;
    const known = this.valueHandlesByCode.get(code);
    const seen = new Set(known ?? []);

    nodes.forEach((node) => {
      if (!node?.handle || seen.has(node.handle)) return;
      seen.add(node.handle);
      known?.add(node.handle);
      if (isSelect) this.appendStreamedOption(group, code, node);
      else this.appendStreamedSwatch(group, node, collapsed);
    });

    group.setAttribute('data-configurable-streamed', '');
  }

  appendStreamedOption(group, code, node) {
    const select = group.querySelector('select[data-configurable-control]');
    if (!select) return;

    const value = node.label?.value ?? '';
    const option = document.createElement('option');
    option.value = value;
    option.textContent = value;
    option.dataset.pivotValueHandle = node.handle;
    option.setAttribute('data-configurable-streamed', '');
    this.optionPool.get(code)?.push(option);
    select.append(option);
  }

  appendStreamedSwatch(group, node, collapsed) {
    const list = group.querySelector('ul.swatches');
    const template = Array.from(list?.children ?? []).find((item) =>
      item.querySelector('input[data-configurable-control]')
    );
    if (!list || !template) return;

    const item = template.cloneNode(true);
    const input = item.querySelector('input[data-configurable-control]');
    const label = item.querySelector('label');
    const value = node.label?.value ?? '';
    const controlId = `${this.id}-stream-${node.handle}`;

    input.checked = false;
    input.id = controlId;
    input.value = value;
    input.dataset.pivotValueHandle = node.handle;
    input.setAttribute('data-configurable-streamed', '');
    item.toggleAttribute('data-configurable-extra', collapsed);
    item.hidden = collapsed;
    if (label) label.setAttribute('for', controlId);

    if (label?.classList.contains('color-swatch')) {
      const fallback = value.split(' ').pop()?.toLowerCase() ?? '';
      const image = node.swatchimage?.reference?.image?.url;
      label.title = value;
      label.style.setProperty('--swatch-background', node.swatchcolor?.value || fallback);
      if (image) {
        label.classList.add('with-image');
        label.style.setProperty('--swatch-background-image', `url(${image})`);
      } else {
        label.classList.remove('with-image');
        label.style.removeProperty('--swatch-background-image');
      }
      const srOnly = label.querySelector('.sr-only');
      if (srOnly) srOnly.textContent = value;
      const tooltip = label.querySelector('.tooltip');
      if (tooltip) tooltip.textContent = value;
    } else if (label) {
      label.title = value;
      label.textContent = value;
    }

    input.addEventListener('change', this.handleSelectorChange);
    list.append(item);
  }

  syncCurrentSelection(group) {
    const select = group.querySelector('select[data-configurable-control]');
    if (select) {
      const match = Array.from(select.options).find((option) =>
        this.currentValueHandles.has(option.dataset.pivotValueHandle)
      );
      if (match) match.selected = true;
      return;
    }

    const input = Array.from(group.querySelectorAll('input[data-configurable-control]')).find((control) =>
      this.currentValueHandles.has(control.dataset.pivotValueHandle)
    );
    if (!input) return;
    input.checked = true;
    const item = input.closest('li');
    if (item) {
      item.hidden = false;
      item.removeAttribute('data-configurable-extra');
    }
  }

  showGroupInFull(group, code) {
    group.hidden = false;

    const select = group.querySelector('select[data-configurable-control]');
    if (select) {
      const pool = this.optionPool.get(code) ?? [];
      const selectedHandle = select.selectedOptions[0]?.dataset.pivotValueHandle ?? this.currentHandles?.get(code);
      if (pool.length) select.replaceChildren(...pool);
      const target =
        Array.from(select.options).find((option) => option.dataset.pivotValueHandle === selectedHandle) ??
        select.options[0];
      if (target) target.selected = true;
      return;
    }

    group.querySelectorAll('input[data-configurable-control]').forEach((input) => {
      const item = input.closest('li');
      if (item) item.hidden = item.hasAttribute('data-configurable-extra');
    });
  }

  disconnectedCallback() {
    this.querySelectorAll('[data-configurable-control]').forEach((control) =>
      control.removeEventListener('change', this.handleSelectorChange)
    );
    this.querySelectorAll('[data-configurable-printing-option]').forEach((option) =>
      option.removeEventListener('change', this.handlePrintingChange)
    );
    this.querySelectorAll('[data-configurable-more]').forEach((button) =>
      button.removeEventListener('click', this.handleMoreColors)
    );
    this.form?.removeEventListener('submit', this.handleSubmit, true);
    this.stickyQuantity?.removeEventListener('click', this.handleStickyQuantityClick);
    window.removeEventListener('pageshow', this.handlePageShow);
    window.removeEventListener('popstate', this.handlePopState);
    document.removeEventListener('click', this.handleOutsideClick);
  }

  get config() {
    return (this._config ??= parseJson(
      this.querySelector('[data-display-group-config]')?.textContent,
      { pivots: [], products: [] }
    ));
  }

  get codes() {
    return (this._codes ??= (this.config.pivots ?? []).map((pivot) => pivot.code));
  }

  get serverGraph() {
    return (this._serverGraph ??= (this.config.products ?? []).map((product) => ({
      ...product,
      pivots: alignPivots(this.codes, this.valueHandlesByCode, product.values ?? []),
    })));
  }

  get valueHandlesByCode() {
    return (this._valueHandlesByCode ??= new Map(
      this.groups.map(({ group, code }) => [
        code,
        new Set(
          Array.from(group.querySelectorAll('[data-pivot-value-handle]'), (el) => el.dataset.pivotValueHandle).filter(
            Boolean
          )
        ),
      ])
    ));
  }

  get groups() {
    return Array.from(this.querySelectorAll('[data-pivot-code]')).map((group) => ({
      group,
      code: group.dataset.pivotCode,
    }));
  }

  get optionPool() {
    return (this._optionPool ??= new Map(
      this.groups.map(({ group, code }) => [
        code,
        Array.from(group.querySelectorAll('select[data-configurable-control] option')),
      ])
    ));
  }

  get selectedPrintingOption() {
    return (
      this.querySelector('[data-configurable-printing-option]:checked')?.value || 'none'
    );
  }

  get isPrintingSelected() {
    return this.selectedPrintingOption === 'custom';
  }

  get selectedQuantity() {
    return this.quantityInput?.value || String(this.minQuantity || 1);
  }

  get minQuantity() {
    return Number(this.dataset.minQty) || 1;
  }

  get defaultQuantity() {
    return Number(this.dataset.defaultQty) || this.minQuantity;
  }

  get quantityRules() {
    return theme.Quantity.rulesFromDataset(this.dataset);
  }

  get maxQuantity() {
    return Number(this.dataset.maxQty) || 0;
  }

  get basePrice() {
    return Number(this.dataset.basePrice) || 0;
  }

  get tiers() {
    if (this._tiers) return this._tiers;
    this._tiers = theme.Quantity.parseTiers(this.dataset.tiers);
    return this._tiers;
  }

  handleStickyQuantityClick(event) {
    if (!this.quantityWrapper) return;

    event.preventDefault();
    this.quantityToggle?.focus({ preventScroll: true });
    this.quantityWrapper.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'center',
    });
  }

  readSelections() {
    return Object.fromEntries(
      this.groups
        .map(({ group, code }) => {
          const control =
            group.querySelector('select[data-configurable-control]')?.selectedOptions[0] ||
            group.querySelector('input[data-configurable-control]:checked');
          const value = control?.dataset.pivotValueHandle;
          return value ? [code, value] : null;
        })
        .filter(Boolean)
    );
  }

  async handleSelectorChange(event) {
    this.updateSelectedLabels();

    const changed = event.target.closest('[data-pivot-code]')?.dataset.pivotCode ?? null;
    const selections = this.readSelections();

    const graph = (await Promise.resolve(this.graphReady).catch(() => null)) ?? this.serverGraph;
    const match = best(
      resolveChangedFirst(this.codes, selections, graph, changed),
      this.dataset.productHandle
    );
    if (match && match.handle !== this.dataset.productHandle && match.url) {
      this.swapSection(match.url);
      return;
    }
    this.updateAvailability();
  }

  // Sections opt into pivot-switch re-rendering by rendering snippets/pdp-hydrate-marker.liquid.
  get hydrateSectionIds() {
    const ids = Array.from(document.querySelectorAll('[data-pdp-hydrate]'), (marker) =>
      marker.closest('.shopify-section')?.id.replace(/^shopify-section-/, '')
    );
    return [...new Set(ids.filter(Boolean))];
  }

  async swapSection(url, { push = true } = {}) {
    const host = this.closest('.shopify-section');
    const ids = this.hydrateSectionIds;
    const targets = ids
      .map((id) => ({ id, el: document.getElementById(`shopify-section-${id}`) }))
      .filter((target) => target.el);

    if (!host?.id || !targets.length || host.hasAttribute('aria-busy')) {
      if (!host?.id) window.location.assign(url);
      return;
    }

    const fetchUrl = `${url}${url.includes('?') ? '&' : '?'}sections=${encodeURIComponent(ids.join(','))}`;
    targets.forEach(({ el }) => {
      el.setAttribute('aria-busy', 'true');
      el.style.opacity = '0.6';
    });

    try {
      // Accept must not be application/json, or a product URL returns { product } and ignores sections=.
      const response = await fetch(fetchUrl, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`sections ${response.status}`);
      const rendered = await response.json();

      // If none of our section ids came back, hard-navigate instead of blanking the page.
      if (!targets.some(({ id }) => typeof rendered[id] === 'string')) {
        throw new Error('no sections in response');
      }

      if (push) window.history.pushState({ configurableSwap: true }, '', url);

      this.applyHeadMetadata(rendered, targets, url);

      // Capture the review rating's width before blanking the section so the SKU doesn't jump.
      window.JamPowerReviews?.reserveWidth?.();

      targets.forEach(({ id, el }) => {
        const markup = rendered[id];
        const doc = markup ? new DOMParser().parseFromString(markup, 'text/html') : undefined;
        // Fall back to the parsed body in case the sections= wrapper div is absent.
        const source = doc?.getElementById(`shopify-section-${id}`) ?? doc?.body;
        // Ignore the hydrate marker and scripts so a self-gated section still counts as empty and hides.
        const hasContent = !!source && Array.from(source.childNodes).some((node) =>
          node.nodeType === Node.ELEMENT_NODE
            ? !node.matches('[data-pdp-hydrate], script')
            : node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== ''
        );

        if (hasContent) {
          el.innerHTML = source.innerHTML;
          el.hidden = false;
        } else {
          el.hidden = true;
        }

        el.style.opacity = '';
        el.removeAttribute('aria-busy');
        el.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true, detail: { sectionId: id } }));
      });

      window.JamPowerReviews?.reload?.();
      this.trackProductView(url);
    } catch {
      window.location.assign(url);
    }
  }

  applyHeadMetadata(rendered, targets, url) {
    const marker = targets
      .map(({ id }) => rendered[id])
      .filter((markup) => typeof markup === 'string')
      .map((markup) => new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-pdp-hydrate]'))
      .find((node) => node?.hasAttribute('data-head-canonical'));
    if (!marker) return;

    const pageTitle = marker.getAttribute('data-head-title');
    const socialTitle = marker.getAttribute('data-head-social-title') || pageTitle;
    const canonical = marker.getAttribute('data-head-canonical') || new URL(url, window.location.origin).href;
    const description = marker.getAttribute('data-head-description');

    if (pageTitle) {
      document.title = this.titleKeepingStoreSuffix(pageTitle);
      this.setMetaContent('meta[property="og:title"]', socialTitle);
      this.setMetaContent('meta[name="twitter:title"]', socialTitle);
    }

    this.setCanonical(canonical);
    this.setMetaContent('meta[property="og:url"]', canonical);

    if (description !== null) {
      this.setMetaContent('meta[name="description"]', description);
      this.setMetaContent('meta[property="og:description"]', description);
      this.setMetaContent('meta[name="twitter:description"]', description);
    }
  }

  titleKeepingStoreSuffix(pageTitle) {
    const currentPageTitle = document.querySelector('[data-pdp-hydrate][data-head-title]')?.getAttribute('data-head-title');
    const hasSuffix = Boolean(currentPageTitle) && document.title.startsWith(currentPageTitle);
    return hasSuffix ? `${pageTitle}${document.title.slice(currentPageTitle.length)}` : pageTitle;
  }

  setCanonical(href) {
    const links = document.head.querySelectorAll('link[rel="canonical"]');
    links.forEach((link, index) => { if (index > 0) link.remove(); });

    const link = links[0] ?? document.head.appendChild(Object.assign(document.createElement('link'), { rel: 'canonical' }));
    link.setAttribute('href', href);
  }

  setMetaContent(selector, content) {
    document.head.querySelector(selector)?.setAttribute('content', content);
  }

  async trackProductView(url) {
    const publish = window.Shopify?.analytics?.publish;
    if (typeof publish !== 'function') return;

    const path = url.split('?')[0];
    try {
      publish('page_viewed', {});

      const response = await fetch(`${path}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const product = await response.json();
      const variant = product.variants?.[0];
      const currencyCode = window.Shopify?.currency?.active ?? 'USD';

      const meta = window.ShopifyAnalytics?.meta;
      if (meta) {
        meta.product = {
          id: product.id,
          gid: `gid://shopify/Product/${product.id}`,
          vendor: product.vendor,
          type: product.type,
          handle: product.handle,
          variants: (product.variants ?? []).map((entry) => ({
            id: entry.id,
            price: entry.price / 100,
            name: entry.name,
            public_title: entry.public_title,
            sku: entry.sku,
          })),
          remote: false,
        };
        meta.page = { pageType: 'product', resourceType: 'product', resourceId: product.id };
      }

      publish('product_viewed', {
        productVariant: {
          id: String(variant?.id ?? ''),
          title: variant?.title ?? product.title,
          sku: variant?.sku ?? '',
          price: { amount: (variant?.price ?? 0) / 100, currencyCode },
          product: {
            id: String(product.id),
            title: product.title,
            vendor: product.vendor,
            type: product.type,
            url: path,
          },
        },
      });
    } catch {
      return;
    }
  }

  handlePopState() {
    this.swapSection(window.location.href, { push: false });
  }

  updateAvailability() {
    const selections = this.readSelections();
    const graphReady = Boolean(this.graph?.length);
    const map = graphReady
      ? new Map(reachable(this.codes, selections, this.graph).map(({ code, values }) => [code, values]))
      : new Map();

    this.groups.forEach(({ group, code }) => {
      if (!graphReady) {
        this.showGroupInFull(group, code);
        return;
      }

      const values = map.get(code);
      const currentHandle = this.currentHandles?.get(code);
      group.hidden = !values || values.size <= 1;
      if (!values) return;

      const select = group.querySelector('select[data-configurable-control]');
      if (select) {
        const pool = this.optionPool.get(code) ?? [];
        const selectedHandle = select.selectedOptions[0]?.dataset.pivotValueHandle ?? currentHandle;
        const kept = pool.filter((option) => {
          const handle = option.dataset.pivotValueHandle;
          return values.has(handle) || handle === currentHandle;
        });
        select.replaceChildren(...kept);
        const target = kept.find((option) => option.dataset.pivotValueHandle === selectedHandle) ?? kept[0];
        if (target) target.selected = true;
      }

      group.querySelectorAll('input[data-configurable-control]').forEach((input) => {
        const handle = input.dataset.pivotValueHandle;
        const item = input.closest('li');
        if (!item) return;
        item.hidden =
          (!values.has(handle) && handle !== currentHandle) ||
          item.hasAttribute('data-configurable-extra');
      });
    });

    this.updateSelectedLabels();
  }

  handlePrintingChange() {
    this.updatePrintingState();
  }

  handleMoreColors(event) {
    const button = event.currentTarget;
    const group = button.closest('[data-pivot-code]');

    group?.querySelectorAll('[data-configurable-extra]').forEach((item) => {
      item.removeAttribute('data-configurable-extra');
    });
    button.hidden = true;
    this.updateAvailability();
  }

  handleSubmit(event) {
    if (!this.isPrintingSelected) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    window.JamDesigner?.open?.({
      productDesignKey: this.dataset.sku || '',
      qty: Number(this.selectedQuantity) || 50,
    });
  }

  handlePageShow(event) {
    if (!event.persisted) return;

    const current = (this.graph ?? this.serverGraph).find((product) => product.handle === this.dataset.productHandle);
    if (current) {
      const wanted = new Set(current.values);
      this.groups.forEach(({ group, code }) => {
        const select = group.querySelector('select[data-configurable-control]');
        if (select) {
          const pool = this.optionPool.get(code);
          if (pool) select.replaceChildren(...pool);
          const match = Array.from(select.options).find((option) =>
            wanted.has(option.dataset.pivotValueHandle)
          );
          if (match) select.value = match.value;
        }
        group.querySelectorAll('input[data-configurable-control]').forEach((input) => {
          input.checked = wanted.has(input.dataset.pivotValueHandle);
        });
      });
    }

    this.resetQuantity();
    this.updateSelectedLabels();
    this.updateAvailability();
  }

  updateSelectedLabels() {
    this.groups.forEach(({ group }) => {
      const label = group.querySelector('[data-configurable-selected-label]');
      if (!label) return;

      const selected =
        group.querySelector('select')?.selectedOptions[0] ||
        group.querySelector('input:checked');
      label.textContent = selected?.value || selected?.textContent?.trim() || '';
    });
  }

  updatePrintingState() {
    this.quantityWrapper?.toggleAttribute('hidden', this.isPrintingSelected);

    this.submitButtons.forEach((button) => {
      button.classList.toggle('is-configurable-printing', this.isPrintingSelected);

      const textElement =
        button.querySelector('[data-configurable-main-label]') ||
        button.querySelector('[data-configurable-sticky-label]') ||
        button.querySelector('.btn-text > span') ||
        button.querySelector('.btn-text');
      if (!textElement) return;

      button.dataset.configurableDefaultText ||= textElement.textContent.trim();
      textElement.textContent = this.isPrintingSelected
        ? this.dataset.startDesigningLabel
        : button.dataset.configurableDefaultText;
    });

    if (this.mainSubmitPrice) {
      this.mainSubmitPrice.hidden = this.isPrintingSelected;
    }

    this.stickyForm?.classList.toggle('is-configurable-printing', this.isPrintingSelected);
    if (this.stickyQuantity && this.quantityDropdown) {
      this.stickyQuantity.hidden = this.isPrintingSelected;
    }
    if (this.stickyTotal) this.stickyTotal.hidden = this.isPrintingSelected;
    if (this.stickyFromPrice) this.stickyFromPrice.hidden = !this.isPrintingSelected;
  }

  updatePurchaseTotals(totalPrice, selectedQuantity) {
    const quantity = selectedQuantity ?? (Number(this.selectedQuantity) || this.minQuantity);
    const resolvedTotal = totalPrice ?? this.unitPriceForQuantity(quantity) * quantity;
    const formattedTotal = this.formatMoney(resolvedTotal);

    if (this.mainSubmitPrice) this.mainSubmitPrice.textContent = `  -  ${formattedTotal}`;
    if (this.stickyQuantityValue) this.stickyQuantityValue.textContent = quantity;
    if (this.stickyTotal) this.stickyTotal.textContent = formattedTotal;

    if (this.userInteracted) {
      const priceAnnouncer = this.closest('product-info')?.querySelector('[data-price-announcer]');
      if (priceAnnouncer && priceAnnouncer.textContent !== formattedTotal) {
        priceAnnouncer.textContent = formattedTotal;
      }
    }
  }

  /* Custom quantity dropdown: per-row unit price, line total, and a custom-entry field that snaps to increment. */
  setupQuantityDropdown() {
    if (!this.quantityDropdown) return;

    this.quantityToggle = this.quantityDropdown.querySelector('[data-quantity-toggle]');
    this.quantityPanel = this.quantityDropdown.querySelector('[data-quantity-panel]');
    this.quantityOptions = Array.from(this.quantityDropdown.querySelectorAll('[data-quantity-option]'));
    this.quantityCustomInput = this.quantityDropdown.querySelector('[data-quantity-custom-input]');
    this.quantityCustomSubmit = this.quantityDropdown.querySelector('[data-quantity-custom-submit]');
    this.quantityCustomHint = this.quantityDropdown.querySelector('[data-quantity-custom-hint]');
    this.quantityCustomHintMessage = this.quantityDropdown.querySelector('[data-quantity-custom-hint-message]');
    this.customQuantityAdjusted = false;

    this.handleOutsideClick = (event) => {
      if (!this.quantityDropdown.contains(event.target)) this.closeQuantityPanel();
    };

    this.quantityToggle?.addEventListener('click', () => this.toggleQuantityPanel());

    this.quantityOptions.forEach((option) => {
      option.addEventListener('click', () => {
        if (this.quantityCustomInput) this.quantityCustomInput.value = '';
        this.customQuantityAdjusted = false;
        this.selectQuantityOption(option);
        this.closeQuantityPanel();
        this.quantityToggle?.focus();
      });
    });

    this.quantityCustomSubmit?.addEventListener('click', () => {
      this.applyCustomQuantity();
      this.closeQuantityPanel();
      this.quantityToggle?.focus();
    });

    this.quantityCustomInput?.addEventListener('input', () => {
      this.customQuantityAdjusted = false;
      this.updateCustomQuantityHint();
    });
    this.quantityCustomInput?.addEventListener('change', () => this.applyCustomQuantity());
    this.quantityCustomInput?.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      this.applyCustomQuantity();
      this.closeQuantityPanel();
      this.quantityToggle?.focus();
    });

    this.quantityDropdown.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.closeQuantityPanel();
        this.quantityToggle?.focus();
      }
    });

    this.applyDefaultQuantity();
    this.updateCustomQuantityHint();
  }

  toggleQuantityPanel() {
    if (this.quantityPanel?.hidden) {
      this.openQuantityPanel();
    } else {
      this.closeQuantityPanel();
    }
  }

  openQuantityPanel() {
    if (!this.quantityPanel) return;
    this.quantityPanel.hidden = false;
    this.quantityToggle?.setAttribute('aria-expanded', 'true');
    document.addEventListener('click', this.handleOutsideClick);
  }

  closeQuantityPanel() {
    if (!this.quantityPanel) return;
    this.quantityPanel.hidden = true;
    this.quantityToggle?.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this.handleOutsideClick);
  }

  selectQuantityOption(option, { silent = false } = {}) {
    const quantity = Number(option.dataset.quantity) || this.minQuantity;
    const unitPrice = Number(option.dataset.unitPrice) || this.unitPriceForQuantity(quantity);
    const totalPrice = Number(option.dataset.totalPrice) || unitPrice * quantity;
    const reduced = option.dataset.reduced === 'true';
    const percentOff = Number(option.dataset.percentOff) || 0;

    this.quantityOptions.forEach((entry) => entry.setAttribute('aria-selected', entry === option ? 'true' : 'false'));

    this.setQuantity(quantity, {
      unitPrice,
      totalPrice,
      reduced,
      percentOff,
      silent,
    });
  }

  applyCustomQuantity() {
    if (!this.quantityCustomInput) return;

    const raw = Number(this.quantityCustomInput.value);
    if (!Number.isFinite(raw) || raw <= 0) {
      this.quantityCustomInput.value = '';
      this.customQuantityAdjusted = false;
      this.updateCustomQuantityHint();
      return;
    }

    const snapped = this.snapQuantity(raw);
    this.customQuantityAdjusted = snapped !== raw;
    this.quantityCustomInput.value = snapped;

    this.quantityOptions.forEach((entry) => entry.setAttribute('aria-selected', 'false'));
    this.setQuantity(snapped);
  }

  snapQuantity(value) {
    return theme.Quantity.snapQuantity(value, this.quantityRules);
  }

  tierForQuantity(quantity) {
    return theme.Quantity.tierForQuantity(quantity, this.tiers);
  }

  unitPriceForQuantity(quantity) {
    return theme.Quantity.unitPriceForQuantity(quantity, this.tiers, this.basePrice);
  }

  setQuantity(quantity, options = {}) {
    const matchedTier = this.tierForQuantity(quantity);
    const unitPrice = options.unitPrice ?? this.unitPriceForQuantity(quantity);
    const totalPrice = options.totalPrice ?? unitPrice * quantity;
    const reduced = options.reduced ?? matchedTier?.reduced ?? false;
    const percentOff = options.percentOff ?? matchedTier?.percentOff ?? 0;

    if (this.quantityInput) {
      this.quantityInput.value = quantity;
      if (!options.silent) {
        this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    this.renderQuantitySummary({
      quantity,
      unitPrice,
      totalPrice,
      reduced,
      percentOff,
    });
  }

  renderQuantitySummary({ quantity, unitPrice, totalPrice, reduced, percentOff }) {
    const qtyEl = this.quantityDropdown?.querySelector('[data-quantity-current-qty]');
    const eachEl = this.quantityDropdown?.querySelector('[data-quantity-current-each]');
    const totalEl = this.quantityDropdown?.querySelector('[data-quantity-current-total]');

    if (qtyEl) qtyEl.textContent = quantity;

    if (eachEl) {
      eachEl.innerHTML = `<span class="quantity-dropdown__each-price">${this.formatAtEach(unitPrice)}</span>`;
      eachEl.classList.toggle('is-reduced', reduced);
    }

    if (totalEl) {
      const saleBadge = reduced && percentOff > 0
        ? `<span class="quantity-dropdown__sale-badge">${percentOff}% off</span>`
        : '';
      totalEl.innerHTML = `<span class="quantity-dropdown__total-price">${this.formatMoney(totalPrice)}</span>${saleBadge}`;
    }
    this.quantityToggle?.classList.toggle('is-reduced', reduced);

    this.updatePurchaseTotals(totalPrice, quantity);
    this.updateCustomQuantityHint({ quantity, unitPrice });
  }

  updateCustomQuantityHint({ quantity, unitPrice } = {}) {
    const hintQty = this.quantityDropdown?.querySelector('[data-quantity-custom-hint-qty]');
    const hintEach = this.quantityDropdown?.querySelector('[data-quantity-custom-hint-each]');
    if (!hintQty || !hintEach) return;

    const raw = Number(this.quantityCustomInput?.value);
    const rawIsValid = Number.isFinite(raw) && raw > 0;
    const snapped = rawIsValid ? this.snapQuantity(raw) : 0;
    const isAdjusted = this.customQuantityAdjusted || (rawIsValid && snapped !== raw);
    const activeIncrement = theme.Quantity.incrementForQuantity(snapped || Number(this.selectedQuantity), this.quantityRules);
    this.quantityCustomHint?.classList.toggle('is-adjusted', isAdjusted);
    if (this.quantityCustomHintMessage) {
      this.quantityCustomHintMessage.textContent = theme.Quantity.customHintMessage({
        isAdjusted,
        adjustedLabel: this.dataset.customQuantityAdjusted,
        hintLabel: this.dataset.customQuantityHint,
        increment: activeIncrement,
      });
    }

    if (rawIsValid) {
      hintQty.textContent = String(snapped);
      hintEach.textContent = this.formatAtEach(this.unitPriceForQuantity(snapped));
      return;
    }

    const resolvedQuantity = quantity ?? Number(this.selectedQuantity);
    hintQty.textContent = String(resolvedQuantity);
    hintEach.textContent = this.formatAtEach(unitPrice ?? this.unitPriceForQuantity(resolvedQuantity));
  }

  formatAtEach(unitPriceCents) {
    return theme.Quantity.formatAtEach(unitPriceCents, this.dataset.atEachLabel);
  }

  applyDefaultQuantity() {
    const selected = this.quantityOptions?.find(
      (option) => Number(option.dataset.quantity) === this.defaultQuantity
    );

    if (selected) {
      this.selectQuantityOption(selected, { silent: true });
      return;
    }

    this.quantityOptions?.forEach((option) => option.setAttribute('aria-selected', 'false'));
    this.setQuantity(this.defaultQuantity, { silent: true });
  }

  resetQuantity() {
    if (this.quantityCustomInput) this.quantityCustomInput.value = '';
    this.customQuantityAdjusted = false;
    this.updateCustomQuantityHint();
    this.applyDefaultQuantity();
  }

  formatMoney(cents) {
    return theme.Quantity.formatMoney(cents);
  }
}

if (!customElements.get('product-configurable-options')) {
  customElements.define('product-configurable-options', ProductConfigurableOptions);
}
